import {RESOURCES} from '../definitions';
import ResourceValidator from './resource-validator';
import {DefaultValidator} from './index';

/**
 * Class to validate complex resource dependency authorizations.
 *
 * // Complex
 * Auth.users.add.delete.and(Auth.profiles.add.read.or(Auth.apiKeys.delete)
 * // Alias
 * Auth.userProfileView
 * @extends ResourceValidator
 */
export default class AliasValidator extends ResourceValidator {
    constructor(unknownProperty, properties) {
        super(unknownProperty, properties);
        this.$alias = properties.$alias;
    }

    /**
     * Creates the proper validator based on instance state
     * @param {string} unknownProperty
     * @return {DefaultValidator}
     */
    $validate(unknownProperty) {
        if (this.alias instanceof Function) {
            // Most probably this validator was already instantiated by now
            // but add to cache so $validate wont be called again
            this.$cachedValidators[unknownProperty] = this.$alias(this.$property, unknownProperty);
            return this.$cachedValidators[unknownProperty];
        } if (this.$alias[unknownProperty]) {
            // An alias of alias probable a resource alias {transaction: {refund: Alias}}
            return this.$addValidator(unknownProperty,
                AliasValidator, {$alias: this.$alias[unknownProperty]});
        } if (RESOURCES[this.$property]) {
            // Access to resource actions over an alias that have same name
            return super.$validate(unknownProperty);
        }
        // I cannot handle this property
        return this.$addValidator(unknownProperty, DefaultValidator);
    }

    /**
     * Checks if the action is allowed based
     * on the $alias function or resources that
     * have the same name as the alias.
     * This allows to create alias for resources and
     * run the right validation.
     *
     * // will evaluate the transaction resource
     * Auth.transaction.isAllowed
     * // will evaluate the alias refund resource
     * Auth.transaction.refund.isAllowed
     * @return {boolean}
     */
    get isAllowed() {
        if (this.$alias instanceof Function) {
            return this.$alias(this.$property, this).isAllowed;
        }
        if (RESOURCES[this.$property]) {
            return super.isAllowed;
        }
        return false;
    }
}
